import defaultStyle from "../../../styles/defaultPageStyle.module.css"
import List from "../../../components/List/List"
import React, { useCallback, useEffect, useRef, useState } from "react"
import { companyMemory, orderMemory } from "../../../Objects/Memory/Memory"
import { OrderEntity } from "../../../Objects/Order/OrderEntity"
import { ORDER_LIST_PAGE_SIZE, type OrderPaginationState, useGetOrder } from "../../../hooks/Order/useGetOrder"
import { useReloadMemory } from "../../../hooks/useReloadMemory"
import { useTranslation } from "react-i18next"
import { OrderPupop } from "./OrderPupop"
import { OrderPupopClose } from "./OrderPupopClose"
import { RodizioOrderPopup } from "./RodizioOrderPopup"
import { faCommentDollar, faFilter } from "@fortawesome/free-solid-svg-icons"
import { converteDateToSql } from "../../../Objects/Generics/Helps"
import { API_ROUTES } from "../../../configuration/RoutesApi"
import THEME from "../../../configuration/Theme"
import { OrderAdvancedFiltersPopup } from "./OrderAdvancedFiltersPopup"
import { getRestaurantNow } from "../../../utils/timeZone"
import { ERequestType } from "../../../Enum/ERequestType"
import { useOnboardingCreatePopup } from "../../../hooks/useOnboardingCreatePopup"
import { useQuickActionPopup } from "../../../hooks/useQuickActionPopup"
import { useSearchParams } from "react-router-dom"

const orderMatchesSearchTerm = (order: OrderEntity, searchTerm: string): boolean => {
  const normalizedSearchTerm = searchTerm.toSearchText()

  if (normalizedSearchTerm.length === 0) {
    return true
  }

  const normalizedDocumentSearchTerm = searchTerm.toDigitsOnly()

  const searchableFields = [
    order.id,
    order.getCustomerDisplayName(),
    order.getCustomerDisplayCpf(),
    order.customerEntity?.personEntity?.rg,
    order.tableEntity?.number,
    order.orderPaymentEntity?.status,
  ]

  if (
    searchableFields.some((field) =>
      String(field ?? "")
        .toSearchText()
        .includes(normalizedSearchTerm),
    )
  ) {
    return true
  }

  return (
    normalizedDocumentSearchTerm.length > 0 &&
    [order.getCustomerDisplayCpf(), order.customerEntity?.personEntity?.rg].some((field) =>
      String(field ?? "")
        .toDigitsOnly()
        .includes(normalizedDocumentSearchTerm),
    )
  )
}

export const OrderPage: React.FC = () => {
  const { t } = useTranslation("order")

  const currentDate = getRestaurantNow(companyMemory.timeZoneId).toInputDate()

  const [searchParams, setSearchParams] = useSearchParams()

  const [enableCommands, setEnableCommands] = useState(false)

  const { handleGetOrder } = useGetOrder()

  const [isFlagLoading] = useState(false)

  const [orderData, setOrderData] = useState<OrderEntity | undefined>(undefined)

  const [rodizioRoundOrder, setRodizioRoundOrder] = useState<OrderEntity | null>(null)

  const { handleReloadToMemory } = useReloadMemory()

  const dateStart = searchParams.get("dateStart")?.trim() || currentDate

  const dateEnd = searchParams.get("dateEnd")?.trim() || currentDate

  const isNegotiationMode = searchParams.get("negotiationMode") === "true"

  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false)

  const [paginationState, setPaginationState] = useState<OrderPaginationState>({
    pageNumber: 1,
    pageSize: ORDER_LIST_PAGE_SIZE,
    totalCount: 0,
    hasNextPage: false,
  })

  const [isLoadingMore, setIsLoadingMore] = useState(false)

  const filterCustomerName = searchParams.get("customerName")?.trim() || ""

  const filterCustomerDocument = searchParams.get("customerDocument")?.trim() || ""

  const filterOrderCode = searchParams.get("orderCode")?.trim() || ""

  const filterOnlyOpenLostOrders = searchParams.get("onlyOpenLostOrders") === "true"

  const filterSearchTerm = searchParams.get("searchTerm")?.trim() || ""

  const [searchTerm, setSearchTerm] = useState(filterSearchTerm)

  const isInitialFilterLoad = useRef(true)

  const loadedOrdersRef = useRef<OrderEntity[]>([])

  const loadedOrdersKeyRef = useRef("")

  const handleOpenEditePromotion = (id: number) => {
    const promotion = orderMemory.findById(id)!

    handleSwitchEditeOrAdd(true)

    setOrderData(promotion)
  }

  const loadOrdersPage = useCallback(
    async (pageNumber: number, append: boolean, filters: URLSearchParams = searchParams) => {
      const startDate = filters.get("dateStart")?.trim() || currentDate

      const endDate = filters.get("dateEnd")?.trim() || currentDate

      const hasDateFilter = filters.has("dateStart") || filters.has("dateEnd")

      const negotiationMode = filters.get("negotiationMode") === "true"

      const onlyOpenLostOrders = filters.get("onlyOpenLostOrders") === "true"

      const querySearchTerm = filters.get("searchTerm")?.trim() || ""

      const customerName = filters.get("customerName")?.trim() || ""

      const customerDocument = filters.get("customerDocument")?.trim() || ""

      const orderCode = filters.get("orderCode")?.trim() || ""

      const route = negotiationMode ? API_ROUTES.ORDER.GET_ALL_NEGOTIATION : API_ROUTES.ORDER.GET_ALL

      const shouldSearchAllDates =
        querySearchTerm.length > 0 &&
        !hasDateFilter &&
        !negotiationMode &&
        !onlyOpenLostOrders &&
        customerName.length === 0 &&
        customerDocument.length === 0 &&
        orderCode.length === 0

      const requestStartDate = shouldSearchAllDates
        ? ""
        : startDate.length === 0
          ? converteDateToSql(getRestaurantNow(companyMemory.timeZoneId))
          : startDate

      const requestEndDate = shouldSearchAllDates
        ? null
        : endDate.length === 0
          ? converteDateToSql(getRestaurantNow(companyMemory.timeZoneId))
          : endDate

      const localSearchKey = [
        shouldSearchAllDates ? "all-dates" : requestStartDate,
        shouldSearchAllDates ? "all-dates" : requestEndDate,
        route,
        negotiationMode,
        onlyOpenLostOrders,
      ].join("|")

      const canUseLocalSearch =
        pageNumber === 1 &&
        !append &&
        querySearchTerm.length > 0 &&
        customerName.length === 0 &&
        customerDocument.length === 0 &&
        orderCode.length === 0 &&
        loadedOrdersKeyRef.current === localSearchKey

      if (canUseLocalSearch) {
        const localMatches = loadedOrdersRef.current.filter((order) => orderMatchesSearchTerm(order, querySearchTerm))

        if (localMatches.length > 0) {
          orderMemory.setList(localMatches)
          setPaginationState({
            pageNumber: 1,
            pageSize: ORDER_LIST_PAGE_SIZE,
            totalCount: localMatches.length,
            hasNextPage: false,
          })

          return
        }
      }

      const nextPaginationState = await handleGetOrder(
        () => {},
        requestStartDate,
        requestEndDate,
        route,
        ERequestType.Restaurant,
        negotiationMode ? false : onlyOpenLostOrders,
        false,
        {
          pageNumber,
          pageSize: ORDER_LIST_PAGE_SIZE,
          append,
          searchTerm: querySearchTerm,
          customerName,
          customerDocument,
          orderCode,
        },
      )

      if (nextPaginationState) {
        setPaginationState(nextPaginationState)

        if (
          querySearchTerm.length === 0 &&
          customerName.length === 0 &&
          customerDocument.length === 0 &&
          orderCode.length === 0
        ) {
          loadedOrdersRef.current = orderMemory.getAll()
          loadedOrdersKeyRef.current = localSearchKey
        }
      }
    },
    [currentDate, handleGetOrder, searchParams],
  )

  const handlerRefresh = async (loadAll: boolean = true, filters: URLSearchParams = searchParams) => {
    if (loadAll) {
      await handleReloadToMemory()
    }

    await loadOrdersPage(1, false, filters)
  }

  const handleLoadMoreOrders = useCallback(async () => {
    if (isLoadingMore || !paginationState.hasNextPage) {
      return
    }

    setIsLoadingMore(true)

    try {
      await loadOrdersPage(paginationState.pageNumber + 1, true)
    } finally {
      setIsLoadingMore(false)
    }
  }, [isLoadingMore, loadOrdersPage, paginationState.hasNextPage, paginationState.pageNumber])

  useEffect(() => {
    const load = async () => {
      const loadAll = isInitialFilterLoad.current

      isInitialFilterLoad.current = false

      await handlerRefresh(loadAll)
    }

    load()
  }, [
    dateStart,
    dateEnd,
    isNegotiationMode,
    filterOnlyOpenLostOrders,
    filterSearchTerm,
    filterCustomerName,
    filterCustomerDocument,
    filterOrderCode,
  ])

  useEffect(() => {
    setSearchTerm(filterSearchTerm)
  }, [filterSearchTerm])

  useEffect(() => {
    const timeout = window.setTimeout(() => {
      const nextSearchParams = new URLSearchParams(searchParams)

      const normalizedSearchTerm = searchTerm.trim()

      normalizedSearchTerm.length > 0
        ? nextSearchParams.set("searchTerm", normalizedSearchTerm)
        : nextSearchParams.delete("searchTerm")

      setSearchParams(nextSearchParams, { replace: true })
    }, 800)

    return () => window.clearTimeout(timeout)
  }, [searchTerm])

  const handleToggleNegotiationMode = () => {
    const nextMode = !isNegotiationMode
    const nextSearchParams = new URLSearchParams(searchParams)

    if (nextMode) {
      nextSearchParams.set("negotiationMode", "true")
    } else {
      nextSearchParams.delete("negotiationMode")
    }

    setSearchParams(nextSearchParams, { replace: true })
  }

  const handleOpenAdvancedFilters = () => {
    setShowAdvancedFilters(true)
  }

  useQuickActionPopup("orders-advanced-filters", handleOpenAdvancedFilters)

  const handleCloseAdvancedFilters = () => {
    setShowAdvancedFilters(false)
  }

  const handleApplyAdvancedFilters = (filters: {
    dateStart: string
    dateEnd: string
    customerName: string
    customerDocument: string
    orderCode: string
    onlyOpenLostOrders: boolean
  }) => {
    const normalizedStart = filters.dateStart.length === 0 ? currentDate : filters.dateStart

    const normalizedEndBase = filters.dateEnd.length === 0 ? currentDate : filters.dateEnd

    const normalizedEnd = normalizedEndBase < normalizedStart ? normalizedStart : normalizedEndBase

    const nextSearchParams = new URLSearchParams(searchParams)

    nextSearchParams.set("dateStart", normalizedStart)

    nextSearchParams.set("dateEnd", normalizedEnd)

    const customerName = filters.customerName.trim()

    const customerDocument = filters.customerDocument.trim()

    const orderCode = filters.orderCode.trim()

    customerName.length > 0
      ? nextSearchParams.set("customerName", customerName)
      : nextSearchParams.delete("customerName")

    customerDocument.length > 0
      ? nextSearchParams.set("customerDocument", customerDocument)
      : nextSearchParams.delete("customerDocument")

    orderCode.length > 0 ? nextSearchParams.set("orderCode", orderCode) : nextSearchParams.delete("orderCode")

    filters.onlyOpenLostOrders
      ? nextSearchParams.set("onlyOpenLostOrders", "true")
      : nextSearchParams.delete("onlyOpenLostOrders")

    setSearchParams(nextSearchParams, { replace: true })

    setShowAdvancedFilters(false)
  }

  const filteredCards = orderMemory
    .getAll()
    .map((order, index) => orderMemory.getCard(order, index, (rodizioOrder) => setRodizioRoundOrder(rodizioOrder)))

  const handleSwitchEditeOrAdd = (status: boolean) => {
    setOrderData(undefined)

    setEnableCommands(status)
  }

  useOnboardingCreatePopup(() => handleSwitchEditeOrAdd(true))

  return (
    <>
      {!isFlagLoading && (
        <>
          <div className={defaultStyle.containerList}>
            <List
              data={filteredCards}
              messageCount={t("list:label_order_page_message_count")}
              messageEmpty={t("list:label_order_page_message_empty")}
              nameFindSearch={t("list:label_order_page_place_holder")}
              handleAddButton={isNegotiationMode ? undefined : () => handleSwitchEditeOrAdd(true)}
              handleRefreshButton={() => handlerRefresh(false)}
              handleExtraFloatButton={handleOpenAdvancedFilters}
              extraFloatButtonDescriptionKey="interactionDescriptions:pages.fluxo.order.orderPage.button.openAdvancedFilters"
              extraFloatButtonIcon={faFilter}
              extraFloatButtonColor={THEME.colors.primary}
              handleSecondaryExtraFloatButton={handleToggleNegotiationMode}
              secondaryExtraFloatButtonDescriptionKey="interactionDescriptions:pages.fluxo.order.orderPage.button.toggleNegotiationMode"
              secondaryExtraFloatButtonIcon={faCommentDollar}
              secondaryExtraFloatButtonColor={
                isNegotiationMode ? THEME.colors.secondary : THEME.colors.buttonBGSaveColor
              }
              showFilterStatus={false}
              handleOnClick={(e) => handleOpenEditePromotion(e)}
              searchValue={searchTerm}
              onSearchChange={setSearchTerm}
              disableLocalSearchFilter
              disableLocalOrdenation
              hasMore={paginationState.hasNextPage}
              isLoadingMore={isLoadingMore}
              onLoadMore={handleLoadMoreOrders}
            />
          </div>

          {enableCommands && ((orderData && !orderData.orderPaymentEntity.isClosed()) || !orderData) && (
            <OrderPupop
              order={orderData}
              title={orderData ? t("title_popup_edite") : t("title_popup_create")}
              onClonse={() => handleSwitchEditeOrAdd(false)}
              onSave={() => handlerRefresh(false)}
            />
          )}

          {enableCommands && orderData && orderData.orderPaymentEntity.isClosed() && (
            <OrderPupopClose
              order={orderData}
              onClonse={() => handleSwitchEditeOrAdd(false)}
              onSave={() => handlerRefresh(false)}
            />
          )}

          {rodizioRoundOrder && (
            <RodizioOrderPopup
              order={rodizioRoundOrder}
              onClose={() => {
                setRodizioRoundOrder(null)
                handlerRefresh(false)
              }}
            />
          )}

          {showAdvancedFilters && (
            <OrderAdvancedFiltersPopup
              initialFilters={{
                dateStart,
                dateEnd,
                customerName: filterCustomerName,
                customerDocument: filterCustomerDocument,
                orderCode: filterOrderCode,
                onlyOpenLostOrders: filterOnlyOpenLostOrders,
              }}
              onApplyFilters={handleApplyAdvancedFilters}
              onClose={handleCloseAdvancedFilters}
            />
          )}
        </>
      )}
    </>
  )
}
